import type { CupUpdate } from "../gen/bazivan/v1/table_pb";
import type { CupRef } from "../net/client";
import { fa } from "../game/cards";

/**
 * لیگ و جام از نگاهِ بازیکن — داده و حساب‌های بی‌React.
 *
 * جدا از Cups.tsx چون سه جا لازمشان دارند: صفحه‌ی جام، نوارِ جام در لابی، و
 * پرده‌ی پایانِ میز. و چون شمارشِ معکوس و مرحله چیزهایی‌اند که با یک عددِ
 * غلط بی‌صدا دروغ می‌گویند، آزمون‌ها همین‌جا را می‌سنجند.
 *
 * جامِ آرنا («جامِ شبِ جمعه»، docs/friday-cup.md) کارزاری است با
 * `format: "arena"` — معمولاً `kind: "cup"`، ولی لیگِ دعوتیِ یک‌هفته‌ای هم آرنا
 * است، پس همه‌جا از روی `format` تصمیم می‌گیریم و نه `kind`. هر چیزی که
 * این‌جا مالِ آرناست، روی لیگ و جامِ دستی اثری ندارد.
 */

export type Phase = "upcoming" | "live" | "closing" | "ended" | "cancelled";

export type Campaign = {
  id: string; kind: "league" | "cup" | "event"; title: string; description: string; game: string;
  banner: string; starts_at_ms: number; ends_at_ms: number; config: Record<string, unknown> | null; entries: number;
  prize_coins?: number;
  // فقط جامِ آرنا — cupView در Backend/cmd/bazivand/cups_api.go
  format?: string; phase?: Phase; entry_fee?: number; prizes?: number[]; win_points?: number; bot_fill_sec?: number;
  /** رقابتِ دعوتی: فقط دعوت‌شده‌ها بازی می‌کنند؛ بقیه جدول را می‌بینند و میزها را تماشا می‌کنند. */
  invitational?: boolean;
  /**
   * جامِ گوهری (از ۱۹ اکتبر ۲۰۲۶): ورودی گوهر است و `prizes` جایزه‌ی همین لحظه از گلدانِ
   * ورودی‌ها — گلدان منهای سهمِ خانه، به نسبتِ `prize_split`. کمتر از `min_paid` ورودیِ
   * پولی، جایزه‌ای نیست و ورودی‌ها برمی‌گردند.
   */
  currency?: "gems" | "coins"; cut_pct?: number; prize_split?: number[]; min_paid?: number;
  pool?: number; paid_entries?: number;
};
export type Row = {
  rank: number; name: string; points: number; played: number; wins: number; me: boolean; eliminated: boolean;
  streak?: number; prize?: number;
};
/**
 * «من» در جدول. برای آرنا با توکن همیشه می‌آید، حتی پیش از ثبت‌نام (rank ۰):
 * `fee` ورودی‌ای است که **همین بازیکن** می‌دهد — اولین جامِ هر کس رایگان است.
 */
export type Me = {
  rank: number; points: number; played: number; wins: number;
  joined?: boolean; streak?: number; fee?: number; first_free?: boolean;
};
export type Detail = { campaign: Campaign; board: Row[]; me: Me | null };

/**
 * لیگ و جامِ شهرها — `GET /v1/cities` (docs/crowd.md، «شهرها»). هر شهر لیگِ این هفته و
 * جامِ نزدیکش را دارد، با صدرنشین و شمارِ میزهای زنده. همه دعوتی‌اند.
 */
export type CityEvent = Campaign & { leader: { name: string; points: number } | null; tables: number };
export type City = { name: string; slug: string; league: CityEvent | null; cup: CityEvent | null };

export const isArena = (c: Campaign): boolean => c.format === "arena";

/** ورودی و جایزه‌ی این رقابت گوهر است؟ */
export const isGems = (c: Campaign): boolean => c.currency === "gems" || c.config?.currency === "gems";

/** واحدِ ورودی و جایزه: «گوهر» یا «سکه». */
export const unitOf = (c: Campaign): string => (isGems(c) ? "گوهر" : "سکه");

/**
 * یک جمله درباره‌ی گلدانِ جامِ گوهری: چقدر جمع شده، یا چرا هنوز جایزه‌ای نیست.
 * جامِ سکه‌ای و دعوتی خالی.
 */
export function poolLine(c: Campaign): string {
  if (!isGems(c) || isInvitational(c)) return "";
  const min = c.min_paid ?? 3;
  const paid = c.paid_entries ?? 0;
  const cut = c.cut_pct ?? 20;
  if (paid < min) {
    return `جایزه گوهر است، از گلدان ورودی‌ها. دست‌کم ${fa(min)} نفر باید ورودی بدهند؛ وگرنه ورودی‌ها برمی‌گردد.`;
  }
  return `گلدان همین حالا ${fa(c.pool ?? 0)} گوهر از ${fa(paid)} نفر دارد. ${fa(cut)}٪ سهم بازی وان است و بقیه جایزه.`;
}

/**
 * رقابتِ دعوتی؟ ردیفِ فهرست پرچمِ تختِ `invitational` دارد؛ config هم همان را
 * دارد و سرورِ کمی کهنه‌تر فقط همان را می‌فرستد.
 */
export const isInvitational = (c: Campaign): boolean =>
  isArena(c) && (c.invitational === true || c.config?.invitational === true);

/** جای دکمه‌ی ثبت‌نام در رقابتِ دعوتی — و جوابِ `invitational` ِ سرور اگر کسی باز هم زد. */
export const INVITE_LINE = "این رقابت دعوتی است؛ می‌توانی جدول و میزهایش را تماشا کنی.";

const ORDER: Record<Phase, number> = { upcoming: 0, live: 1, closing: 2, ended: 3, cancelled: 4 };

/**
 * مرحله‌ی جامِ آرنا در لحظه‌ی now — همان cup.Phase ِ سرور.
 *
 * مرحله‌ی سرور مالِ لحظه‌ی درخواست است، و صفحه‌ای که از عصر باز مانده ساعتِ
 * ۲۱ باید خودش «زنده» شود. پس هر کدام جلوتر است برنده است: ساعتِ مرورگر
 * مرحله را جلو می‌بَرد، ولی جامی را که سرور «تمام» گفته زنده نمی‌کند.
 */
export function phaseAt(c: Campaign, now = Date.now()): Phase {
  const mine: Phase = now < c.starts_at_ms ? "upcoming" : now < c.ends_at_ms ? "live" : "closing";
  const theirs = c.phase;
  return theirs && ORDER[theirs] > ORDER[mine] ? theirs : mine;
}

/**
 * «۲ روز و ۳ ساعت»، «۱ ساعت و ۲۰ دقیقه»، «۲۵ دقیقه» — دو واحدِ درشت و بس.
 * رو به بالا گرد می‌شود تا تا لحظه‌ی آخر «۱ دقیقه» بماند و نه «۰ دقیقه».
 */
export function span(ms: number): string {
  const min = Math.ceil(Math.max(0, ms) / 60_000);
  if (min < 1) return "چند لحظه";
  const d = Math.floor(min / 1440), h = Math.floor((min % 1440) / 60), m = min % 60;
  if (d > 0) return h > 0 ? `${fa(d)} روز و ${fa(h)} ساعت` : `${fa(d)} روز`;
  if (h > 0) return m > 0 ? `${fa(h)} ساعت و ${fa(m)} دقیقه` : `${fa(h)} ساعت`;
  return `${fa(m)} دقیقه`;
}

// ساعتِ جام ساعتِ تهران است («پنجشنبه ۲۱:۰۰»)، هر جای دنیا که مرورگر باشد:
// بازیکنی که از استانبول بازی می‌کند هم همان ساعتی را می‌خواهد که روی پوستر است.
const TZ = "Asia/Tehran";
const wdFmt = new Intl.DateTimeFormat("fa-IR", { weekday: "long", timeZone: TZ });
const hmFmt = new Intl.DateTimeFormat("fa-IR", { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: TZ });
const dayFmt = new Intl.DateTimeFormat("fa-IR", { day: "numeric", month: "long", timeZone: TZ });
// کلیدِ روزِ تهران، برای «شروع و پایان یک روزند؟» — لاتین تا مقایسه ساده بماند.
const dayKey = new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit", timeZone: TZ });

export const weekday = (ms: number): string => wdFmt.format(ms);
export const clock = (ms: number): string => hmFmt.format(ms);
/** «پنجشنبه ساعتِ ۲۱:۰۰» */
export const at = (ms: number): string => `${weekday(ms)} ساعت ${clock(ms)}`;

/** خطِ جامِ کاشیِ شهر: «جام زنده است»، «جامِ امشب ساعتِ ۲۱:۰۰»، «جام: شنبه ۲۱:۰۰». */
export function cityCupLine(c: Campaign | null, now = Date.now()): string {
  if (!c) return "";
  switch (phaseAt(c, now)) {
    case "live": return "جام زنده است";
    case "closing": return "جام: آخرین میزها";
    case "upcoming":
      return dayKey.format(c.starts_at_ms) === dayKey.format(now)
        ? `جام امشب ساعت ${clock(c.starts_at_ms)}`
        : `جام: ${weekday(c.starts_at_ms)} ${clock(c.starts_at_ms)}`;
    default: return "";
  }
}

/** وضعیتِ جامِ آرنا در یک جمله — بالای صفحه‌ی جام. */
export function arenaStatus(c: Campaign, now = Date.now()): string {
  switch (phaseAt(c, now)) {
    case "upcoming": return `${span(c.starts_at_ms - now)} تا شروع · ${at(c.starts_at_ms)}`;
    case "live": return `${span(c.ends_at_ms - now)} تا پایان`;
    case "closing": return "آخرین میزها در حال بازی‌اند. جایزه‌ها به‌زودی";
    case "ended": return "تمام شد";
    default: return "لغو شد";
  }
}

/**
 * زمانِ کارتِ فهرست. برای لیگ و جامِ دستی همان «۳ روز مانده» تا پایان؛ برای
 * آرنا به مرحله بسته است، چون «۶ روز مانده» برای جامی که هنوز شروع نشده
 * یعنی «زود باش» — درست برعکسِ واقعیت.
 */
export function listWhen(c: Campaign, now = Date.now()): string {
  if (!isArena(c)) return left(c.ends_at_ms, now);
  switch (phaseAt(c, now)) {
    case "upcoming": return `${span(c.starts_at_ms - now)} تا شروع`;
    // «،» و نه «·»: «·» کنارِ رقمِ فارسی صفر (۰) دیده می‌شود — «زنده · ۲ ساعت» «زنده ۲۰ ساعت» خوانده می‌شد
    case "live": return `زنده، ${span(c.ends_at_ms - now)} تا پایان`;
    case "closing": return "آخرین میزها در جریان";
    case "ended": return "تمام شد";
    default: return "لغو شد";
  }
}

/** «۳ روز مانده» — چیزی که بازیکن از لیگ و جامِ دستی واقعاً می‌خواهد بداند. */
export function left(endMs: number, now = Date.now()): string {
  const ms = endMs - now;
  if (ms <= 0) return "تمام شد";
  const h = Math.floor(ms / 3600000);
  if (h < 1) return "کمتر از یک ساعت مانده";
  if (h < 48) return fa(h) + " ساعت مانده";
  return fa(Math.floor(h / 24)) + " روز مانده";
}

/** امتیازِ یک برد و بردِ سرِ میزِ رباتی — همان Rules.Points ِ سرور. */
export function winPoints(c: Campaign): { full: number; half: number } {
  const full = c.win_points ?? 2;
  return { full, half: full <= 1 ? full : Math.floor(full / 2) };
}

/**
 * قاعده در سه خط: امتیاز، رشته، زمان. بیشتر از این را کسی سرِ صفحه‌ی جام
 * نمی‌خوانَد؛ ریزِ قاعده (هم‌امتیازی، میزِ نیمه‌تمام) در docs/friday-cup.md است.
 */
export function rules(c: Campaign): string[] {
  const p = winPoints(c);
  const s = c.starts_at_ms, e = c.ends_at_ms;
  const long = span(e - s);
  // جامِ هفتگی «هر پنجشنبه» است؛ جامِ مناسبتی یک بار، پس روز و ماهش را می‌گوییم؛
  // و لیگِ چندروزه دو روزِ جدا دارد. جامِ دعوتی هم series دارد ولی در چند روزِ
  // هفته برگزار می‌شود، پس «هر دوشنبه» برایش نیمی از حقیقت است: روزِ همین دوره.
  const series = c.config?.series;
  // جامِ شهری یک روزِ ثابت دارد، پس «هر شنبه» برایش راست است.
  const weekly = typeof series === "string" && series !== "" && (!isInvitational(c) || series === "city-cup");
  const oneDay = dayKey.format(s) === dayKey.format(Math.max(s, e - 1));
  // «شب‌های مافیا» هر شب است (config.daily) و از نیمه‌شب می‌گذرد: «هر شب ساعتِ ۲۳:۰۰ تا ۰۰:۳۰»
  const daily = c.config?.daily === true && weekly;
  const mafia = c.game === "mafia";
  // پایانِ نیمه‌شب (لیگِ هفته: شنبه ۰۰:۰۰) «جمعه … ساعتِ ۲۴:۰۰» است، نه «شنبه … ۰۰:۰۰».
  const midnight = clock(e) === clock(Date.UTC(2026, 0, 1, 20, 30)); // ۲۰:۳۰ ِ UTC = ۰۰:۰۰ ِ تهران
  const eDay = midnight ? e - 60_000 : e;
  const eClock = midnight ? "۲۴:۰۰" : clock(e);
  const when = daily
    ? `هر شب از ساعت ${clock(s)} تا ${clock(e)}`
    : !oneDay
    ? `از ${weekday(s)} ${dayFmt.format(s)} ساعت ${clock(s)} تا ${weekday(eDay)} ${dayFmt.format(eDay)} ساعت ${eClock}`
    : weekly
      ? `هر ${weekday(s)} ساعت ${clock(s)} تا ${clock(e)}`
      : `${weekday(s)} ${dayFmt.format(s)}، ساعت ${clock(s)} تا ${clock(e)}`;
  const win = mafia ? "هر بار که تیمت (شهر یا مافیا) ببرد" : "هر برد";
  return [
    p.half < p.full
      ? `${win} ${fa(p.full)} امتیاز؛ سر میزی که ربات دارد ${fa(p.half)} امتیاز.`
      : `${win} ${fa(p.full)} امتیاز.`,
    "از برد سوم پشت‌سرِهم هر برد دوبرابر 🔥؛ باخت یا ترک میز رشته را صفر می‌کند.",
    `${when} به وقت تهران — ${long}، هر چند ${mafia ? "بازی" : "دست"} که بخواهی.`,
  ];
}

/**
 * فهرستِ جایزه‌ها: سه رتبه‌ی اول جدا، و بقیه‌ی پله‌های هم‌مبلغ یک خط —
 * «رتبه‌ی ۴ تا ۱۰». ده خطِ «۲۰۰ سکه» پشتِ هم فقط جا می‌گیرد.
 */
export function prizeLines(prizes: readonly number[]): { label: string; coins: number }[] {
  const medal = ["🥇 اول", "🥈 دوم", "🥉 سوم"];
  const out: { label: string; coins: number }[] = [];
  let i = 0;
  for (; i < Math.min(3, prizes.length); i++) {
    if (prizes[i]! > 0) out.push({ label: medal[i]!, coins: prizes[i]! });
  }
  while (i < prizes.length) {
    let j = i;
    while (j + 1 < prizes.length && prizes[j + 1] === prizes[i]) j++;
    if (prizes[i]! > 0) out.push({ label: i === j ? `رتبه‌ی ${fa(i + 1)}` : `رتبه‌ی ${fa(i + 1)} تا ${fa(j + 1)}`, coins: prizes[i]! });
    i = j + 1;
  }
  return out;
}

/**
 * ورودی‌ای که این بازیکن می‌دهد. `me.fee` از سرور است و «اولین جامت رایگان» را
 * در خودش دارد؛ بی توکن (me خالی) همان ورودیِ اعلام‌شده.
 */
export const feeFor = (c: Campaign, me: Me | null): number => me?.fee ?? c.entry_fee ?? 0;

/** «+۲ امتیازِ جام · رتبه‌ی ۳ از ۲۵ 🔥» — خطِ نتیجه‌ی یک میزِ جام. */
export function cupLine(u: CupUpdate): string {
  const head = u.delta > 0n ? `+${fa(u.delta)} امتیاز جام` : "این میز امتیازی نداشت";
  const rank = u.rank > 0 ? ` · رتبه‌ی ${fa(u.rank)} از ${fa(u.entries)}` : "";
  // 🔥 یعنی «بردِ بعدی دوبرابر است» — همان وقتی که رشته به دو رسیده.
  return head + rank + (u.streak >= 2 ? " 🔥" : "");
}

/** پانویسِ نتیجه: چرا امتیاز همین شد. */
export function cupNote(u: CupUpdate): string {
  const bits: string[] = [];
  if (u.doubled) bits.push("برد دوبرابر!");
  if (u.half) bits.push("میز ربات داشت، امتیاز برد نصف شد");
  if (!u.win) bits.push("رشته‌ی برد از نو");
  bits.push(`جمع تو ${fa(u.points)} امتیاز`);
  return bits.join(" · ");
}

/** کدِ خطای ثبت‌نام → جمله‌ای که بازیکن با آن کاری بکند. */
export function joinError(code: string, fee: number): string {
  switch (code) {
    case "account_required":
    case "unauthorized":
      return "جام فقط برای حساب ثبت‌شده است تا جایزه‌ات با پاک‌شدن مرورگر از بین نرود.";
    case "insufficient_coins": return `ورودی ${fa(fee)} سکه است و سکه‌ات کافی نیست.`;
    case "insufficient_gems": return `ورودی ${fa(fee)} گوهر است و گوهرت کافی نیست. در فروشگاه سکه‌ات را گوهر کن.`;
    case "update_required": return "این جام نسخه‌ی تازه‌ی بازی را می‌خواهد؛ صفحه را تازه کن.";
    case "cup_closed": return "ثبت‌نام این جام بسته شد.";
    case "not_found": return "این جام دیگر نیست.";
    case "invitational": return INVITE_LINE;
    case "too_many": return "چند بار پشت هم زدی؛ کمی صبر کن و دوباره بزن.";
    case "network": return "به سرور نرسید؛ دوباره بزن.";
    default: return "ثبت‌نام نشد؛ دوباره بزن.";
  }
}

/** همان چیزی که صفِ جام و «بازیِ بعدی» از یک کارزار لازم دارند. */
export const cupRef = (c: Campaign): CupRef =>
  ({ id: c.id, game: c.game || "hokm", title: c.title, endsAtMs: c.ends_at_ms });
